import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Grid, Box, Typography, Link, MenuItem } from "@material-ui/core";
import PropTypes from "prop-types";
import clsx from "clsx";
import parse from "html-react-parser";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import AmoTextField from "components/inputs/AmoTextField";
import AmoTooltip from "components/AmoTooltip";
import FileUploadList from "components/FileUploadList";

import {
  formIds,
  projectReportResultsCodes as formCodes,
  projectReportGeneralInfoCodes as formGeneralCodes,
} from "constants/formContentManagement";
import { useFormManagement } from "hooks/formManagementHook";
import { useMunicipalContext } from "contexts/MunicipalContext";
import { fileAssociations } from "constants/fileTypes";

const useStyles = makeStyles(() => ({
  indicatorHeader: {
    fontSize: "1.5rem",
  },
  marginBottomHeader: {
    marginBottom: "1rem",
  },
  marginRightHeader: {
    marginRight: "1rem",
  },
  headerStyles: {
    marginTop: "1.5rem",
  },
  textRow: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
  },
  tooltipContainer: {
    marginLeft: "0.5rem",
    marginTop: "0.25rem",
  },
  inputContainer: {
    width: "20rem",
  },
  inRowInputContainer: {
    width: "30rem",
  },
  linksContainer: {
    width: "20rem",
    marginTop: "2.5rem",
  },
  fileUploadTopMargin: {
    // 40px
    marginTop: "2.5rem",
  },
  otherResults: {
    marginTop: "2rem",
  },
}));
/**
 * A form component for editing Project Report Result Housing part
 *
 * @param {object} props - object containing props for this component
 * @param {boolean} props.disabled - allows inputs to be disabled when reporting year doesn't match end date
 * @param {number} props.projectId - object containing projectId
 * @param {number} props.municipalityId - object containing municipalityId
 * @param {object} props.initialFiles - object containing initial files array
 * @param {object} props.housingSchema - yup schema for the outcome form
 * @param {object} props.defaultValues - default values for the outcome form
 * @param {Function} props.validateHousing - function that determines if the housing form is valid or not
 * @param {object} props.prevFormValues - object that holds the previous form value before a save event
 * @param {Function} props.dirtyFieldHook - hook that returns the forms dirtyFields to the parent component
 * @param {string} props.housing - object containing housing
 * @param {number} props.housingUnits - object containing housingUnits
 * @param {number} props.housingAffordableUnits - object containing housingAffordableUnits
 * @param {string} props.housingMethodology - object containing housingMethodology
 * @param {string} props.housingOtherInformation - object containing housingOtherInformation
 * @param {string} props.housingLinks - object containing housingLinks
 * @param {boolean} props.shouldShowHousing - object containing shouldShowHousing
 * @param {Function} props.onFormRender - function called when the project outputs form is rendered (params: RHF utils)
 * @param {Function} props.onGoToGeneralTab - function called when need to change housing value
 * @param {boolean} props.isAnnualReportTab - Adjusts the styling of the form slightly depending on whether it's being rendered as an annual report tab
 *
 * @returns - Project Outputs form component
 */

const Housing = ({
  disabled,
  projectId,
  municipalityId,
  initialFiles,
  housingSchema,
  defaultValues,
  validateHousing,
  prevFormValues,
  dirtyFieldHook,
  housing,
  housingUnits,
  housingAffordableUnits,
  housingMethodology,
  housingOtherInformation,
  housingLinks,
  shouldShowHousing,
  onFormRender,
  onGoToGeneralTab,
  isAnnualReportTab,
}) => {
  const classes = useStyles();
  const { getMunicipality } = useMunicipalContext();

  const [files, setFiles] = useState([]);
  const [fileIsLoaded, setFileIsLoaded] = useState(false);

  useEffect(() => {
    setFiles(initialFiles || []);
    setFileIsLoaded(true);
  }, [initialFiles]);

  const handleFilesChange = (value) => setFiles(value);

  const { formFields } = useFormManagement(formIds.projectReportResults);
  const { formFields: formGeneralFields } = useFormManagement(
    formIds.projectReportGeneralInfo
  );

  const previousFormFields = useRef(null);
  const [formWasReset, setFormWasReset] = useState(false);

  const { control, getValues, formState, watch, reset, trigger } = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(housingSchema),
  });

  const { isDirty, isValid, dirtyFields } = formState;

  const currentHousingValue = watch("housing");

  /**
   * Load saved values from API
   */
  const loadSavedData = () => {
    const values = {
      ...defaultValues,
      housing,
      housingUnits,
      housingAffordableUnits,
      housingMethodology,
      housingOtherInformation,
      housingLinks,
    };

    if (prevFormValues && Object.keys(prevFormValues).length > 0) {
      previousFormFields.current = prevFormValues;
    }

    reset(values);
    setFormWasReset(true);
  };

  /**
   * Initial mount
   */
  useEffect(() => {
    onFormRender(getValues);
    loadSavedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * After reset → revalidate
   */
  useEffect(() => {
    if (!formWasReset) return;

    trigger();
    previousFormFields.current = null;
    setFormWasReset(false);
  }, [formWasReset, trigger]);

  /**
   * Bubble up validity
   */
  useEffect(() => {
    validateHousing({ isDirty, isValid });
  }, [isDirty, isValid, validateHousing]);

  /**
   * Bubble up dirty fields
   */
  useEffect(() => {
    dirtyFieldHook(dirtyFields);
  }, [dirtyFields, dirtyFieldHook]);

  if (!shouldShowHousing) {
    return null;
  }

  return (
    <>
      {/* Title */}
      <Grid
        item
        className={clsx(classes.indicatorHeader, classes.headerStyles)}
      >
        <Typography variant="h5">
          {parse(formFields[formCodes.titleHousing]?.text || "")}
        </Typography>
      </Grid>

      {/* Description */}
      <Grid item className={classes.headerStyles}>
        <Typography variant="body1">
          {parse(
            formFields[formCodes.descriptionHousingImpactTitle]?.text || ""
          )}
        </Typography>
      </Grid>

      {/* Housing impact */}
      <Box
        className={clsx(
          classes.textRow,
          classes.headerStyles,
          classes.marginBottomHeader
        )}
      >
        <Typography variant="body2">
          {formFields[formCodes.descriptionHousingFirst]?.text}
        </Typography>
        {formGeneralFields[formGeneralCodes.descriptionHousingFirst]
          ?.tooltipText && (
          <Box className={classes.tooltipContainer}>
            <AmoTooltip
              tooltipText={
                formGeneralFields[formGeneralCodes.descriptionHousingFirst]
                  ?.tooltipText
              }
            />
          </Box>
        )}
      </Box>

      <Box className={classes.textRow}>
        <AmoTextField
          select
          placeholder="Housing impact"
          selectPlaceholderValue="None"
          id="housing-id"
          disabled={isAnnualReportTab}
          control={control}
          name="housing"
          className={clsx(classes.inputContainer, classes.marginRightHeader)}
          label={
            formGeneralFields[formGeneralCodes.fieldHousingImpact]?.text || ""
          }
        >
          <MenuItem key="housing-impact-true" value="Yes">
            Yes
          </MenuItem>
          <MenuItem key="housing-impact-false" value="No">
            No
          </MenuItem>
        </AmoTextField>

        {isAnnualReportTab && (
          <Typography variant="body1" component="span">
            You previously indicated that this project enables or preserves
            housing. Please provide more information. To change your answer
            please go back to&nbsp;
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              component="button"
              variant="body2"
              color="primary"
              onClick={onGoToGeneralTab}
              underline="always"
              style={{
                padding: 0,
                whiteSpace: "nowrap",
                verticalAlign: "baseline",
              }}
            >
              General Tab
            </Link>
          </Typography>
        )}
      </Box>

      {/* Conditional fields */}
      {currentHousingValue === "Yes" && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box className={clsx(classes.textRow, classes.headerStyles)}>
                <Typography variant="body2">
                  {formFields[formCodes.descriptionHousingSecond]?.text}
                </Typography>
              </Box>

              <Box className={clsx(classes.textRow, classes.otherResults)}>
                <AmoTextField
                  id="housing-units-id"
                  disabled={disabled}
                  control={control}
                  name="housingUnits"
                  className={classes.inRowInputContainer}
                  label={formFields[formCodes.fieldValueUnits]?.text || ""}
                />
                {formFields[formCodes.fieldValueUnits]?.tooltipText !==
                  null && (
                  <Box className={classes.tooltipContainer}>
                    <AmoTooltip
                      tooltipTitle={null}
                      tooltipText={
                        formFields[formCodes.fieldValueUnits]?.tooltipText
                      }
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className={clsx(classes.textRow, classes.headerStyles)}>
                <Typography variant="body2">
                  {formFields[formCodes.descriptionHousingThird]?.text}
                </Typography>
              </Box>

              <Box className={clsx(classes.textRow, classes.otherResults)}>
                <AmoTextField
                  id="housing-affordable-units-id"
                  disabled={disabled}
                  control={control}
                  name="housingAffordableUnits"
                  className={classes.inRowInputContainer}
                  label={formFields[formCodes.fieldValueAffordable]?.text || ""}
                />
                {formFields[formCodes.fieldValueAffordable]?.tooltipText !==
                  null && (
                  <Box className={classes.tooltipContainer}>
                    <AmoTooltip
                      tooltipTitle={null}
                      tooltipText={
                        formFields[formCodes.fieldValueAffordable]?.tooltipText
                      }
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box className={clsx(classes.textRow, classes.headerStyles)}>
            <Typography variant="body2">
              {formFields[formCodes.descriptionHousingMethodology]?.text}
            </Typography>
          </Box>

          <Box className={clsx(classes.textRow, classes.otherResults)}>
            <AmoTextField
              id="housing-methodology-id"
              multiline
              rows={1}
              rowsMax={10}
              control={control}
              disabled={disabled}
              helperText={parse(
                formFields[formCodes.fieldMethodologyHousing]?.helperText || ""
              )}
              name="housingMethodology"
              data-testid="housing-other-information-id-test"
              fullWidth
              label={formFields[formCodes.fieldMethodologyHousing]?.text || ""}
              characterLimit={2000}
            />
            {formFields[formCodes.fieldMethodologyHousing]?.tooltipText !==
              null && (
              <Box className={classes.tooltipContainer}>
                <AmoTooltip
                  tooltipTitle={null}
                  tooltipText={
                    formFields[formCodes.fieldMethodologyHousing]?.tooltipText
                  }
                />
              </Box>
            )}
          </Box>

          <Box className={clsx(classes.textRow, classes.headerStyles)}>
            <Typography variant="body2">
              {formFields[formCodes.descriptionHousingImpactDetails]?.text}
            </Typography>
          </Box>

          <Box className={clsx(classes.textRow, classes.otherResults)}>
            <AmoTextField
              id="housing-other-information-id"
              multiline
              rows={1}
              rowsMax={10}
              control={control}
              disabled={disabled}
              helperText={parse(
                formFields[formCodes.fieldOtherInformationHousing]
                  ?.helperText || ""
              )}
              name="housingOtherInformation"
              data-testid="housing-other-information-id-test"
              fullWidth
              label={
                formFields[formCodes.fieldOtherInformationHousing]?.text || ""
              }
              characterLimit={2000}
            />
            {formFields[formCodes.fieldOtherInformationHousing]?.tooltipText !==
              null && (
              <Box className={classes.tooltipContainer}>
                <AmoTooltip
                  tooltipTitle={null}
                  tooltipText={
                    formFields[formCodes.fieldOtherInformationHousing]
                      ?.tooltipText
                  }
                />
              </Box>
            )}
          </Box>

          <Box className={clsx(classes.textRow, classes.headerStyles)}>
            <Typography variant="body2">
              {formFields[formCodes.descriptionHousingShare]?.text}
            </Typography>
          </Box>

          <Grid item className={classes.fileUploadTopMargin}>
            {fileIsLoaded && projectId && projectId !== "new" && (
              <FileUploadList
                fileAssociationId={fileAssociations.Housing}
                fileAssociationObjectId={parseInt(projectId, 10)}
                initialValue={files}
                municipalityId={municipalityId ?? getMunicipality()?.id}
                onChange={handleFilesChange}
                multiple
                readonly={disabled}
                synchronized
              />
            )}
          </Grid>

          <Box className={clsx(classes.textRow, classes.otherResults)}>
            <AmoTextField
              id="housing-links-id"
              multiline
              rows={1}
              rowsMax={10}
              control={control}
              disabled={disabled}
              helperText={parse(
                formFields[formCodes.fieldLinksInformationHousing]
                  ?.helperText || ""
              )}
              name="housingLinks"
              data-testid="housing-links-id-test"
              fullWidth
              label={
                formFields[formCodes.fieldLinksInformationHousing]?.text || ""
              }
              characterLimit={2000}
            />
            {formFields[formCodes.fieldLinksInformationHousing]?.tooltipText !==
              null && (
              <Box className={classes.tooltipContainer}>
                <AmoTooltip
                  tooltipTitle={null}
                  tooltipText={
                    formFields[formCodes.fieldLinksInformationHousing]
                      ?.tooltipText
                  }
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
};

Housing.propTypes = {
  disabled: PropTypes.bool.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  municipalityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialFiles: PropTypes.arrayOf(PropTypes.number),
  housingSchema: PropTypes.shape().isRequired,
  defaultValues: PropTypes.shape().isRequired,
  validateHousing: PropTypes.func.isRequired,
  prevFormValues: PropTypes.shape().isRequired,
  dirtyFieldHook: PropTypes.func.isRequired,
  housing: PropTypes.string.isRequired,
  housingUnits: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  housingAffordableUnits: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]).isRequired,
  housingMethodology: PropTypes.string.isRequired,
  housingOtherInformation: PropTypes.string.isRequired,
  housingLinks: PropTypes.string.isRequired,
  shouldShowHousing: PropTypes.bool.isRequired,
  onFormRender: PropTypes.func.isRequired,
  onGoToGeneralTab: PropTypes.func.isRequired,
  isAnnualReportTab: PropTypes.bool,
};

Housing.defaultProps = {
  initialFiles: [],
  isAnnualReportTab: true,
  municipalityId: null,
};

export default Housing;
